import React from "react";

type DivProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: React.ReactNode;
};

function cn(...parts: (string | undefined | false)[]) {
  return parts.filter(Boolean).join(" ");
}

/** Próximo irmão após “está alugado?” — espelha `IsRentedContainer + div` do styled-components */
const IS_RENTED_BLOCK = "edit-property-is-rented-block";

export const Container: React.FC<DivProps> = ({ className, ...props }) => (
  <div className={cn("", className)} {...props} />
);

export const Content: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "mx-auto flex max-w-[1200px] flex-col items-center justify-center",
      className
    )}
    {...props}
  />
);

export const EditPropertyContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn("mt-6 w-full px-3 py-6 md:px-6", className)}
    {...props}
  />
);

/** Edição: 1ª faixa — tipo + ranking + alugado (empilhados); 2ª faixa — andar | ZAP (filhos seguintes) */
export const PropertyTypeAndIsRentedContainer: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn("mt-4 flex w-full flex-col gap-10", className)}
    {...props}
  />
);

export const PropertyTypesContentColumn: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex min-w-0 w-full flex-col items-start justify-start",
      className
    )}
    {...props}
  />
);

export const PropertyTypesContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "mx-auto mt-4 flex w-full max-w-4xl flex-row flex-wrap items-center justify-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-4 max-md:flex-col max-md:items-stretch max-md:justify-start",
      className
    )}
    {...props}
  />
);

export const PropertyTypesContent: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex items-center justify-center max-md:mt-2 [&>label]:ml-1.5 [&>label]:mr-16 [&>label]:cursor-pointer",
      className
    )}
    {...props}
  />
);

/** Linha compacta de rádios (andar / ZAP): menos espaço, evita quebra em 2 colunas estreitas */
export const PropertyTypesContentCompact: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex shrink-0 items-center justify-center max-md:mt-1 [&>label]:ml-1 [&>label]:mr-2 [&>label]:cursor-pointer [&>label]:whitespace-nowrap",
      className
    )}
    {...props}
  />
);

export const IsRentedContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex w-full max-w-full flex-col items-start justify-items-start",
      IS_RENTED_BLOCK,
      className
    )}
    {...props}
  />
);

export const RentAndIsRentedRadioContent: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className, type = "button", ...props }) => (
  <button
    type={type}
    className={cn(
      "flex items-center justify-center border-0 bg-transparent p-0 [&>p]:mr-2",
      className
    )}
    {...props}
  />
);

export const PropertyOwnerAndPropertyConsultantContainer: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "my-12 flex w-full items-start justify-start max-md:flex-col",
      "[&_.css-2b097c-container]:mt-3 [&_.css-2b097c-container]:w-full",
      "[&_[class*='-control']]:rounded-md [&_[class*='-control']]:shadow-none",
      "[&_.css-1okebmr-indicatorSeparator]:hidden",
      className
    )}
    {...props}
  />
);

export const PropertyOwnerContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex w-[45%] flex-col items-start justify-start max-md:mb-4 max-md:w-full",
      "[&>span]:text-[10px] [&>span]:font-normal [&>span]:leading-snug [&>span]:text-slate-500",
      "[&>p]:flex [&>p]:flex-col [&>p]:items-start [&>p]:justify-start",
      "[&>p>p]:text-[10px] [&>p>p]:font-bold",
      className
    )}
    {...props}
  />
);

export const FormContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "relative w-full min-w-0",
      "[&>p]:mb-3 [&>p]:mt-6 [&>p]:text-sm [&>p]:font-semibold [&>p]:text-slate-700",
      "[&>input]:mb-1 [&>input]:w-full [&>input]:cursor-default [&>input]:rounded-md [&>input]:border [&>input]:border-solid [&>input]:border-slate-200 [&>input]:bg-white [&>input]:px-3 [&>input]:py-2.5 [&>input]:text-sm [&>input]:text-slate-800 [&>input]:shadow-none [&>input]:outline-none [&>input]:transition [&>input:focus]:border-slate-400 [&>input:focus]:ring-0",
      "[&_.css-2b097c-container]:w-full [&_.css-2b097c-container:last-child]:mb-16",
      "[&_[class*='-control']]:rounded-md [&_[class*='-control']]:border [&_[class*='-control']]:border-solid [&_[class*='-control']]:border-slate-200 [&_[class*='-control']]:bg-white [&_[class*='-control']]:shadow-none",
      "[&_.css-1okebmr-indicatorSeparator]:hidden",
      "[&_.MuiAutocomplete-root]:mb-4",
      "[&>#users]:absolute [&>#users]:left-0 [&>#users]:right-0 [&>#users]:top-full [&>#users]:z-[10000] [&>#users]:mt-1 [&>#users]:flex [&>#users]:max-h-[350px] [&>#users]:flex-col [&>#users]:items-stretch [&>#users]:justify-start [&>#users]:overflow-y-auto [&>#users]:rounded-md [&>#users]:border [&>#users]:border-slate-200 [&>#users]:bg-white [&>#users]:shadow-lg",
      "[&>#users>button]:w-full [&>#users>button]:border-0 [&>#users>button]:bg-white [&>#users>button]:px-4 [&>#users>button]:py-2.5 [&>#users>button]:text-left [&>#users>button]:text-sm [&>#users>button]:text-slate-800 [&>#users>button]:transition-colors [&>#users>button]:duration-200 [&>#users>button:hover]:bg-slate-50",
      "max-md:[&_.css-2b097c-container]:w-full max-md:[&>input]:w-full",
      className
    )}
    {...props}
  />
);

export const PropertyConsultantContainer: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "ml-auto mt-3 flex w-[45%] flex-col items-start justify-start max-md:ml-0 max-md:w-full",
      "[&>p]:mb-4 [&>p]:flex [&>p]:flex-col [&>p]:items-start [&>p]:justify-start [&>p]:text-sm [&>p]:font-semibold [&>p]:text-slate-700",
      "[&>p>p]:text-[10px] [&>p>p]:font-bold",
      className
    )}
    {...props}
  />
);

export const RoomsSuitesBathroomsGarageContainer: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "my-[18px] flex w-full items-center justify-between max-md:flex-col max-md:items-start max-md:justify-start",
      className
    )}
    {...props}
  />
);

export const RoomsSuitesBathroomsGarageContent: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-col items-start justify-start",
      "[&>label]:mb-2 [&>label]:text-sm [&>label]:font-semibold [&>label]:text-slate-700",
      "[&>label>p]:text-[10px] [&>label>p]:font-bold",
      "[&_.css-2b097c-container]:w-[213px]",
      "[&_[class*='-control']]:rounded-md [&_[class*='-control']]:border [&_[class*='-control']]:border-solid [&_[class*='-control']]:border-slate-200 [&_[class*='-control']]:shadow-none",
      "[&_.css-1okebmr-indicatorSeparator]:hidden",
      "max-md:w-full max-md:[&>label]:mt-4 max-md:[&_.css-2b097c-container]:w-full",
      className
    )}
    {...props}
  />
);

export const RentIptuCondominiumValueAreaContainer: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "my-12 grid w-full grid-cols-1 items-start gap-x-8 gap-y-10 sm:grid-cols-2 xl:grid-cols-3",
      className
    )}
    {...props}
  />
);

export const RentIptuCondominiumValueAreaContent: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex min-w-0 w-full flex-col items-stretch justify-start",
      "[&>label]:mb-2 [&>label]:block [&>label]:text-sm [&>label]:font-semibold [&>label]:leading-snug [&>label]:text-slate-700 [&>label]:break-words",
      "[&>label>p]:text-[10px] [&>label>p]:font-bold",
      "[&>p]:mt-2 [&>p]:text-xs [&>p]:leading-relaxed [&>p]:text-slate-500",
      /* Currency / AreaInput / MaskedInput — mesmo visual do PropertyValue no cadastro */
      "[&_input]:mb-0 [&_input]:w-full [&_input]:min-w-0 [&_input]:cursor-text [&_input]:rounded-md [&_input]:border [&_input]:border-solid [&_input]:border-slate-200 [&_input]:bg-white [&_input]:px-3 [&_input]:py-2.5 [&_input]:text-sm [&_input]:text-slate-800 [&_input]:shadow-none [&_input]:outline-none [&_input]:transition [&_input:focus]:border-slate-400 [&_input:focus]:ring-0",
      className
    )}
    {...props}
  />
);

export const CondominiumNameFieldWrapper: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "my-5 w-full",
      "[&>p]:mb-2 [&>p]:text-sm [&>p]:font-semibold [&>p]:text-slate-700",
      "[&>p>p]:text-[10px] [&>p>p]:font-bold",
      "[&>input]:w-full [&>input]:rounded-md [&>input]:border [&>input]:border-solid [&>input]:border-slate-200 [&>input]:bg-white [&>input]:px-3 [&>input]:py-2 [&>input]:text-sm [&>input]:text-slate-800 [&>input]:shadow-none [&>input]:outline-none [&>input]:transition [&>input:focus]:border-slate-400 [&>input:focus]:ring-0",
      className
    )}
    {...props}
  />
);

export const AllowCatAndIsFunishedContainer: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "mx-auto mb-12 mt-[18px] flex w-[90%] flex-row items-center justify-items-center max-md:flex-col",
      "md:!ml-4 md:!mr-auto max-md:!ml-0 max-md:!mt-4",
      className
    )}
    {...props}
  />
);

export const GuaranteesContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "my-4 mb-8 flex w-full flex-col items-start justify-start",
      "[&>label]:text-sm [&>label]:font-semibold [&>label]:text-slate-700",
      "[&>#inputs-container]:mt-4 [&>#inputs-container]:grid [&>#inputs-container]:w-full [&>#inputs-container]:grid-cols-[19%_19%_19%_19%_19%] [&>#inputs-container]:gap-y-3 [&>#inputs-container]:gap-x-[1.25%] [&>#inputs-container]:rounded-md [&>#inputs-container]:border [&>#inputs-container]:border-slate-200 [&>#inputs-container]:bg-slate-50 [&>#inputs-container]:p-4",
      "[&>#inputs-container>#input-container]:flex [&>#inputs-container>#input-container]:gap-[5%]",
      className
    )}
    {...props}
  />
);

export const AllowCatAndIsFunishedContent: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex w-full flex-col items-center justify-items-center max-md:flex-col",
      "[&+div]:ml-4 max-md:[&+div]:ml-0 max-md:[&+div]:mt-4",
      className
    )}
    {...props}
  />
);

export const IsRentedRadioContent: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "my-3 mb-[18px] ml-2 flex w-[200px] items-center justify-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1",
      "[&>p]:mx-1 [&>p]:cursor-pointer [&>p]:text-sm [&>p]:text-slate-700",
      "[&_.isRentedRadio]:text-slate-600",
      "[&_.MuiRadio-colorSecondary.Mui-checked]:text-[#6EA5FF]",
      className
    )}
    {...props}
  />
);

export const RadioContentToDescriptionForm: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "my-1.5 mb-[18px] flex w-full max-w-[220px] items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white p-1",
      "[&>p]:mx-1 [&>p]:cursor-pointer [&>p]:text-sm [&>p]:text-slate-700",
      className
    )}
    {...props}
  />
);

export const CharacteristicFormContainer: React.FC<
  React.FormHTMLAttributes<HTMLFormElement> & { children?: React.ReactNode }
> = ({ className, ...props }) => (
  <form
    className={cn(
      "w-full rounded-md border border-slate-200 bg-white p-5 sm:p-8",
      "[&>h3]:text-center [&>h3]:text-[28px] [&>h3]:text-slate-800",
      className
    )}
    {...props}
  />
);

export const CharacteristicContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex items-start justify-start max-md:flex-col [&>*+*]:ml-auto max-md:[&>*+*]:ml-0",
      className
    )}
    {...props}
  />
);

export const CharacteristicContent: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "mt-6 flex w-[48%] flex-col items-start justify-start max-md:w-full",
      className
    )}
    {...props}
  />
);

export const BigCharacteristicContent: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "mt-6 flex w-full flex-col items-start justify-start",
      className
    )}
    {...props}
  />
);

export const CharacteristicInitialContainer: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "w-full border-b border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-800",
      className
    )}
    {...props}
  />
);

export const CharacteristicRadiosContainer: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex w-full flex-wrap content-start items-start justify-start gap-x-6 gap-y-2 border-0 bg-white px-4 pb-4 pt-4",
      className
    )}
    {...props}
  />
);

export const RadioContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "ml-0 flex w-[210px] flex-col items-start justify-start sm:ml-2",
      "[&>p]:text-sm [&>p]:font-medium [&>p]:text-slate-700",
      className
    )}
    {...props}
  />
);

export const RadioContent: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "my-1.5 mb-2 flex w-[200px] items-center justify-start gap-1 rounded-lg border border-slate-200 bg-slate-50/80 py-2 pl-2 pr-2",
      "[&>p]:mx-1 [&>p]:cursor-pointer [&>p]:text-sm [&>p]:text-slate-700",
      className
    )}
    {...props}
  />
);

export const DropZoneContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "mt-4 flex min-h-[128px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/70 px-4 py-6 text-center text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-100/80",
      "[&>p]:flex [&>p]:items-center [&>p]:justify-center",
      className
    )}
    {...props}
  />
);

export const PhotoContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex w-full flex-wrap items-start justify-start",
      className
    )}
    {...props}
  />
);

export const PropertyRankContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex w-full max-w-[220px] flex-col items-stretch",
      "[&>label]:mb-2 [&>label]:mt-0 [&>label]:text-sm [&>label]:font-semibold [&>label]:text-slate-700",
      "[&_.css-2b097c-container]:w-full",
      "[&_[class*='-control']]:rounded-md [&_[class*='-control']]:border [&_[class*='-control']]:border-solid [&_[class*='-control']]:border-slate-200",
      className
    )}
    {...props}
  />
);

export const PublishOnImovelWebButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className, type = "button", ...props }) => (
  <button
    type={type}
    className={cn(
      "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border-0 bg-[#DD6435] px-4 py-3 text-sm font-bold text-white shadow-none transition hover:bg-[#c4552b]",
      "[&>p]:m-0 [&>p]:whitespace-nowrap [&>p]:text-white",
      className
    )}
    {...props}
  />
);

export const PropertyTitleContainer: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex w-full flex-col",
      "[&>p]:mb-3 [&>p]:mt-6 [&>p]:text-sm [&>p]:font-semibold [&>p]:text-slate-700",
      "[&>input]:mb-2 [&>input]:w-full [&>input]:cursor-default [&>input]:rounded-md [&>input]:border [&>input]:border-solid [&>input]:border-slate-200 [&>input]:bg-white [&>input]:px-3 [&>input]:py-2.5 [&>input]:text-sm [&>input]:text-slate-800 [&>input]:shadow-none [&>input]:outline-none [&>input]:transition [&>input:focus]:border-slate-400 [&>input:focus]:ring-0",
      className
    )}
    {...props}
  />
);

export const AIPreviewModalOverlay: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "fixed inset-0 z-[9999] flex items-center justify-center bg-black/75",
      className
    )}
    {...props}
  />
);

export const AIPreviewModalContent: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "relative w-[min(96vw,1100px)] max-h-[90vh] overflow-y-auto rounded-xl bg-white p-5 shadow-xl sm:p-6",
      className
    )}
    {...props}
  />
);

export const AIPreviewModalHeader: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "mb-4 flex items-center justify-between border-b border-[#eee] pb-3",
      className
    )}
    {...props}
  />
);

export const AIPreviewModalTitle: React.FC<
  React.PropsWithChildren<React.HTMLAttributes<HTMLHeadingElement>>
> = ({ className, children, ...props }) => (
  <h2 className={cn("m-0 text-[#DD6435]", className)} {...props}>
    {children}
  </h2>
);

export const CloseModalButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className, type = "button", ...props }) => (
  <button
    type={type}
    className={cn(
      "cursor-pointer border-0 bg-transparent text-[32px] leading-none text-[#999] hover:text-[#333]",
      className
    )}
    {...props}
  />
);

export const AIPreviewGrid: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "grid grid-cols-1 gap-4",
      className
    )}
    {...props}
  />
);

export const AIPreviewCard: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "rounded-[10px] border-2 border-[#DD6435] bg-white p-5 shadow-[0_6px_16px_rgba(0,0,0,0.1)]",
      className
    )}
    {...props}
  />
);

export const AIPreviewImagesRow: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "mb-5 flex gap-5 max-md:flex-col max-md:gap-4",
      className
    )}
    {...props}
  />
);

export const AIPreviewImageBox: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex-1",
      "[&>p]:mb-2.5 [&>p]:text-sm [&>p]:font-semibold [&>p]:text-[#333]",
      "[&>img]:h-[360px] [&>img]:w-full [&>img]:rounded-lg [&>img]:border [&>img]:border-[#eee] [&>img]:bg-[#f9f9f9] [&>img]:object-contain",
      className
    )}
    {...props}
  />
);

export const AIPreviewActionButtons: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "mt-4 flex gap-3 [&>button]:flex-1 [&>button]:cursor-pointer [&>button]:rounded-md [&>button]:border-0 [&>button]:py-3.5 [&>button]:text-[15px] [&>button]:font-semibold",
      className
    )}
    {...props}
  />
);

export const ApplyAIButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className, type = "button", ...props }) => (
  <button
    type={type}
    className={cn(
      "bg-[#28a745] text-white hover:bg-[#218838]",
      className
    )}
    {...props}
  />
);

export const DiscardAIButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className, type = "button", ...props }) => (
  <button
    type={type}
    className={cn(
      "bg-[#dc3545] text-white hover:bg-[#c82333]",
      className
    )}
    {...props}
  />
);

export const OpenAIPreviewsButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className, type = "button", ...props }) => (
  <button
    type={type}
    className={cn(
      "fixed bottom-[30px] right-[30px] z-[9998] flex cursor-pointer items-center gap-2 rounded-[50px] border-0 bg-[#DD6435] px-6 py-3.5 text-[15px] font-semibold text-white shadow-[0_4px_15px_rgba(221,100,53,0.4)] hover:-translate-y-0.5 hover:bg-[#c14e2a]",
      className
    )}
    {...props}
  />
);
